'use client';

type Props = {
  event: LeagueEvent;
};

import { Button, SearchMD } from '@v2/ui';

import { RoomConfiguration } from '@components/RoomConfigurator';
import { ILocationResult } from '@components/searchCity';
import { PACKAGE_STORAGE } from '@config/storageKeys';
import useStorage from '@hooks/useStorage';
import { LeagueEvent } from '@interfaces/APITravel';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { normalizeDateToUtcNoonIso } from '@v2/utils/date';
import { useFormik } from 'formik';
import { useBuildPackagesStore } from 'src/stores/build-package-store';
import { useMediaQuery } from 'usehooks-ts';
import {
  coerceHotelSearchArea,
  stadiumLocationFromEvent,
  type HotelSearchArea,
} from '../../hotel-search-area';
import { isMapCoordinate } from '../../hotel-presentation';
import { confirmedRooms, readHotelStorage } from '../../room-prefill';
import { invalidatePropertySearch } from '../../util';
import { BaseCheckInOutFilter } from './BaseFilters/BaseCheckinOutFilter';
import { BaseLocationFilter } from './BaseFilters/BaseLocationFilter';
import { BaseRoomsFilter } from './BaseFilters/BaseRoomsFilter';
import { MobileFilter } from './MobileFilter';

export type HeaderFormikProps = {
  location?: ILocationResult | undefined | null;
  stadiumLocation?: ILocationResult | undefined | null;
  hotelSearchArea?: HotelSearchArea;
  hotelStartDate: string | Date;
  hotelEndDate: string | Date;
  roomConfigurations: RoomConfiguration[];
};

export function Header({ event }: Props) {
  const storage = useStorage();

  const {
    location,
    stadiumLocation,
    hotelSearchArea,
    hotelEndDate,
    hotelStartDate,
    roomConfigurations,
  } = useBuildPackagesStore((state) => ({
    location: state.location,
    stadiumLocation: state.stadiumLocation,
    hotelSearchArea: state.hotelSearchArea,
    hotelStartDate: state.hotelStartDate,
    hotelEndDate: state.hotelEndDate,
    roomConfigurations: state.roomConfigurations,
  }));

  const commitHotelSearch = (val: HeaderFormikProps) => {
    const nextStart = normalizeDateToUtcNoonIso(val.hotelStartDate);
    const nextEnd = normalizeDateToUtcNoonIso(val.hotelEndDate);
    const latitude = val.location?.latitude;
    const longitude = val.location?.longitude;
    if (
      latitude == null ||
      longitude == null ||
      !isMapCoordinate(Number(latitude), Number(longitude))
    ) {
      return;
    }
    const requestedArea = coerceHotelSearchArea(val.hotelSearchArea);
    const nextStadium =
      stadiumLocationFromEvent(event) ??
      val.stadiumLocation ??
      stadiumLocation ??
      null;
    const packageData = readHotelStorage(storage, PACKAGE_STORAGE);

    storage.setItem(
      PACKAGE_STORAGE,
      JSON.stringify({
        ...packageData,
        ...val,
        hotelSearchArea: requestedArea,
        stadiumLocation: nextStadium,
        hotelStartDate: nextStart,
        hotelEndDate: nextEnd,
      })
    );

    useBuildPackagesStore.setState({
      ...val,
      hotelSearchArea: requestedArea,
      stadiumLocation: nextStadium,
      hotelStartDate: nextStart,
      hotelEndDate: nextEnd,
    });
    useSearchHotelStore.setState({ roomSelections: [] });
    invalidatePropertySearch();
  };

  const formik = useFormik<HeaderFormikProps>({
    initialValues: {
      location: location,
      stadiumLocation: stadiumLocation ?? stadiumLocationFromEvent(event),
      hotelSearchArea: coerceHotelSearchArea(hotelSearchArea),
      hotelStartDate: hotelStartDate,
      hotelEndDate: hotelEndDate,
      roomConfigurations: roomConfigurations,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { roomConfigurations?: string; location?: string } = {};
      if (!confirmedRooms(values.roomConfigurations)) {
        errors.roomConfigurations =
          'Choose an age for each child before searching.';
      }
      const latitude = values.location?.latitude;
      const longitude = values.location?.longitude;
      if (
        latitude == null ||
        longitude == null ||
        !isMapCoordinate(Number(latitude), Number(longitude))
      ) {
        errors.location =
          'Choose an address or landmark from the suggestions before searching.';
      }
      return errors;
    },
    onSubmit: (val) => {
      commitHotelSearch(val);
    },
  });

  const matches = useMediaQuery('(min-width: 1024px)');

  if (!matches) return <MobileFilter event={event} formik={formik} />;

  return (
    <>
      <form className="p-5" onSubmit={formik.handleSubmit}>
        <h2 className="mb-5 text-2xl font-bold text-gray-800">
          Choose your hotel
        </h2>
        {formik.submitCount > 0 && formik.errors.roomConfigurations && (
          <p role="alert" className="mb-3 text-sm text-primary-600">
            Choose an age for each child before searching.
          </p>
        )}
        {formik.submitCount > 0 &&
          typeof formik.errors.location === 'string' && (
            <p role="alert" className="mb-3 text-sm text-primary-600">
              {formik.errors.location}
            </p>
          )}
        <div className="flex items-center gap-x-3">
          <BaseLocationFilter
            formik={formik}
            event={event}
            onApplyArea={(next) => {
              const nextValues = {
                ...formik.values,
                hotelSearchArea: next.area,
                location: next.location,
              };
              void formik.setValues(nextValues);
              commitHotelSearch(nextValues);
            }}
          />
          <BaseCheckInOutFilter
            formik={formik}
            event={event}
            onApplyDateRange={({ from, to }) => {
              const nextValues = {
                ...formik.values,
                hotelStartDate: from,
                hotelEndDate: to,
              };
              void formik.setValues(nextValues);
              commitHotelSearch(nextValues);
            }}
          />
          <BaseRoomsFilter formik={formik} />
          <Button
            className="h-16 shrink-0 rounded-xl px-5"
            type="submit"
            aria-label="Search availability"
          >
            <SearchMD className="text-white" />
          </Button>
        </div>
      </form>
    </>
  );
}
